import React, { useState, ChangeEvent, useMemo } from 'react'
import {
  TextField,
  Autocomplete,
  debounce,
  CircularProgress,
  Box,
  Typography,
  Alert,
  Drawer,
  Fab,
  IconButton,
  Collapse,
} from '@mui/material'
import apiClient from '@/utils/apiClient'
import { Close, GTranslate } from '@mui/icons-material'
import { LANGUAGE_OPTIONS } from '@/constants/options'
import { enqueueSnackbar } from 'notistack'
import { useLogger } from '@/lib/axiom/client'
import { getCookie } from '@/utils/Utils'
import { useTranslations } from 'next-intl'
import { BORDER_GREY } from '@/constants/colors'

type InputTranslatorProps = {
  targetLanguage: string
}

const InputTranslator: React.FC<InputTranslatorProps> = ({ targetLanguage }) => {
  const t = useTranslations('HOMEPAGE')
  const [inputValue, setInputValue] = useState<string>('')
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [openDrawer, setOpenDrawer] = useState<boolean>(false)
  const [openAlert, setOpenAlert] = useState<boolean>(true)
  const logger = useLogger().with({ userId: getCookie('mbm-user-id') })
  const targetLanguageLabel = useMemo(() => LANGUAGE_OPTIONS.find((lang) => lang.code === targetLanguage)?.label, [targetLanguage])

  const fetchSuggestions = async (text: string) => {
    try {
      const response = await apiClient.get('/api/translate', {
        params: { text, lang: targetLanguage },
      })

      if (response.status === 200) {
        setSuggestions(response.data.suggestions || [])
      } else {
        setSuggestions([])
      }
    } catch (error) {
      enqueueSnackbar('Error fetching suggestions', {
        variant: 'error',
        TransitionProps: { direction: 'up' },
      })
      setSuggestions([])
    } finally {
      setLoading(false)
    }
  }

  const debouncedFetchSuggestions = useMemo(() => debounce(fetchSuggestions, 500), [targetLanguage])

  const handleInputChange = (_event: ChangeEvent<{}>, value: string) => {
    setInputValue(value)
    if (value) {
      setLoading(true)
      debouncedFetchSuggestions(value)
    } else {
      setSuggestions([])
    }
  }

  const handleOptionClick = async (option: string) => {
    await navigator.clipboard.writeText(option)
    logger.info('Copied to clipboard - Input Translate', { option })
    enqueueSnackbar(t('COPIED_TO_CLIPBOARD'), {
      variant: 'default',
      TransitionProps: { direction: 'up' },
    })
  }

  return (
    <>
      <Fab
        size="medium"
        color="primary"
        aria-label="translate"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={() => setOpenDrawer(true)}
      >
        <GTranslate />
      </Fab>
      <Drawer
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        anchor="bottom"
        variant="persistent"
        slotProps={{
          paper: {
            sx: {
              p: 3,
              borderRadius: '12px 12px 0 0',
              border: `1px solid ${BORDER_GREY}`,
              maxWidth: '700px',
              width: '100%',
              margin: '0 auto',
            },
          },
        }}
      >
        <IconButton aria-label="close" onClick={() => setOpenDrawer(false)} sx={{ position: 'absolute', top: 8, right: 8 }}>
          <Close fontSize="small" />
        </IconButton>
        <Typography variant="body1" gutterBottom sx={{
          fontWeight: "bold"
        }}>
          {t('TRANSLATE_INPUT_LABEL')}
        </Typography>
        <Autocomplete
          freeSolo
          fullWidth
          loading={loading}
          filterOptions={(x) => x}
          size="small"
          options={suggestions}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          onChange={(_event, option) => option && handleOptionClick(option)}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              placeholder={t('TRANSLATE_INPUT_PLACEHOLDER', { language: targetLanguageLabel || 'English' })}
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps.input,
                  type: 'search',
                  sx: {
                    '& input::-webkit-search-cancel-button': {
                      display: 'none',
                    },
                  },
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="primary" size={20} /> : null}
                      {params.slotProps.input.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
        />
        <Collapse in={openAlert}>
          <Alert severity="info" sx={{ mt: 2 }} onClose={() => setOpenAlert(false)}>
            {t('INPUT_TRANSLATE_ALERT', { language: targetLanguageLabel || 'English' })}
          </Alert>
        </Collapse>
      </Drawer>
    </>
  );
}

export default InputTranslator
